import { ITranslationSchema, enforcedTranslationMapping, ITranslations } from '../../interfaces/ITranslationFields';
import { ILinkTranslation } from '../../interfaces/Translations';
import { snakeToCamelCase } from '../../utils/SnakeToCamelCase';

type translationType = string | ILinkTranslation;

interface IGenericMap {
  [key: string]: ITranslationsSet;
}

interface ITranslationsSet {
  [key: string]: translationType;
}

class TranslationRepository {
  translationProxy: ITranslationSchema;

  proxyPlaceholderHandler = {
    get: (target: ITranslationsSet, prop: string) => {
      return prop in target ? target[prop] : this.wrapPlaceholder(target[prop], prop);
    },
  };

  constructor() {
    this.translationProxy = new Proxy<ITranslationSchema>({}, this.proxyPlaceholderHandler);
  }

  loadTranslations(source?: IGenericMap): void {
    const translationSchema = source || window.site?.translations || {};
    const flattenedTranslations = this.flattenTranslatationSchema(translationSchema);

    this.translationProxy = new Proxy<ITranslationSchema>(flattenedTranslations, this.proxyPlaceholderHandler);
  }

  /**
   * @param {Array} translationKeys - Array of strings representing translation keys.
   * @param {Object} translationSchema - Object representing translation schema.
   *
   * This function iterates over `translationSchema`, creating a new object with child values or placeholders.
   * It then checks `translationKeys` against this object, adding any missing keys with placeholders.
   *
   * @returns {Object} - An object containing all keys from `translationSchema` and `translationKeys` with
   * corresponding values or placeholders strings if no value was provided in `translationSchema`.
   */
  private flattenTranslatationSchema = (translationSchema: IGenericMap = {} ): ITranslationSchema => {
    return Object.entries(translationSchema).reduce((parentAcc, [parentKey, parentvalue]) => {
      // Merge each parent value to a set of translation fields
      const translationFields = Object.entries(parentvalue).reduce((childAcc: { [key: string]: translationType }, [childKey, childValue]) => {
        const keyName = enforcedTranslationMapping.get(`${parentKey}.${childKey}`) || snakeToCamelCase(childKey);
        // If the child value is not an empty string use it or add a placeholder  
        childAcc[keyName] = this.wrapPlaceholder(childValue, `${parentKey}.${childKey}`);

        return childAcc;
      }, {});
      // Merge the parent accumulator with the translation fields
      return Object.assign(parentAcc, translationFields);
    }, {}) as ITranslationSchema;
  };

  /**
   * @param {Array} translationKeys - Array of strings for lookup in `translationSchema`.
   *
   * This function iterates over `translationKeys`, by creating a new object with child values or placeholders from `translationSchema`.
   *
   * @returns {Object} - An object containing all keys from `translationKeys` with
   * corresponding values or placeholders strings if key does exisit in `translationSchema`.
   */
  listRequiredTranslationSchema = (translationKeys: string[]): ITranslationSchema => {
    const translationSchema = this.translationSchema as { [key: string]: string };
    return translationKeys.reduce((acc: { [key: string]: translationType }, key) => {
      if (translationSchema.hasOwnProperty(key)) {
        acc[key] = translationSchema[key];
      } else {
        acc[key] = this.wrapPlaceholder(acc[key], key);
      }
      return acc;
    }, {});
  };

  private wrapPlaceholder(value: translationType, keyName: string): translationType {
    if (typeof value === 'string' && value) {
      return value;
    }

    if (value instanceof Array) {
      return value;
    }

    if (keyName === 'undefined') {
      return value;
    }

    if (typeof value === 'object' && value !== null) {
      return {
        ...value,
        title: value?.title || `~${keyName}~`,
      };
    }

    return `~${keyName}~`;
  }

  get translationSchema(): ITranslations {
    return this.translationProxy as ITranslations;
  }
}

export default TranslationRepository;
